import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { DataBoxesContainer } from 'src/components/atoms/DataBoxesContainer';
import { useFilterContext } from 'src/contexts/FilterContext';

type PageProps = {
  filterBar?: React.ReactElement | null;
  keyMetrics?: JSX.Element;
  children: React.ReactNode;
  pageTitle?: string;
  pageSubtitle?: string;
  showHeaderRightSide?: boolean;
  showGenAIInsights?: boolean;
  extraButtons?: React.ReactElement | null;
  genAiInsights?: React.ReactElement | null;
};

export const Page: React.FC<PageProps> = ({
  filterBar,
  keyMetrics,
  pageTitle = '',
  children,
  pageSubtitle,
  extraButtons,
  genAiInsights,
}) => {
  const bg = useColorModeValue(
    'background.light.highest',
    'background.dark.lowest',
  );
  const pageTitleColor = useColorModeValue(
    'text.light.pageTitle',
    'text.dark.pageTitle',
  );
  const pageSubtitleColor = useColorModeValue(
    'text.light.secondary',
    'text.dark.secondary',
  );
  const location = useLocation();
  const { changeCurrentModelByParametersId, modelVersionsState } =
    useFilterContext();
  const processedRef = useRef<string | null>(null);

  // Scroll to top when switching tabs
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  // Handle model selection from navigation state (e.g., when coming from Goals)
  useEffect(() => {
    const state = location.state as { modelParametersId?: string } | null;
    const modelParametersId = state?.modelParametersId;

    if (
      modelParametersId &&
      modelVersionsState.modelVersions.length > 0 &&
      processedRef.current !== modelParametersId
    ) {
      processedRef.current = modelParametersId;
      changeCurrentModelByParametersId(modelParametersId);
    }
  }, [
    location.state,
    modelVersionsState.modelVersions,
    changeCurrentModelByParametersId,
  ]);

  return (
    <Flex w="100%" direction="column" background={bg}>
      <Box zIndex={10} top={0} pos="sticky">
        {filterBar}
      </Box>
      <Flex
        flex="1"
        direction="column"
        px={{ base: '20px', md: '40px' }}
        py={12}>
        <Flex direction="column" gap={4} alignItems="flex-end" mb={3}>
          <Flex
            justifyContent={{ base: 'flex-start', lg: 'space-between' }}
            alignItems={{ base: 'stretch', lg: 'flex-end' }}
            direction={{ base: 'column', lg: 'row' }}
            gap={{ base: 6, lg: 0 }}
            w="100%"
            minH="100px">
            <Flex direction="column" gap={4} flex={{ base: 1, lg: 'none' }}>
              <Text
                fontSize={32}
                fontFamily="marketingPage"
                fontWeight="bold"
                color={pageTitleColor}>
                {pageTitle}
              </Text>
              {pageSubtitle && (
                <Text
                  fontSize={16}
                  color={pageSubtitleColor}
                  fontFamily="marketingPage">
                  {pageSubtitle}
                </Text>
              )}
            </Flex>
            {extraButtons}
          </Flex>
        </Flex>
        {genAiInsights}
        {keyMetrics}
        <DataBoxesContainer>{children}</DataBoxesContainer>
      </Flex>
    </Flex>
  );
};
